import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Clock, MapPin, AlertTriangle, Activity, Lock } from 'lucide-react';
import { useAdminSecurity } from '@/hooks/useAdminSecurity';
import { formatDistanceToNow } from 'date-fns';

export const AdminSecurityPanel = () => {
  const {
    settings,
    auditLogs,
    loading,
    updateIPRestrictions,
    updateSessionTimeout,
    toggleAdminLock,
    refetchAuditLogs
  } = useAdminSecurity();

  const [newIPRange, setNewIPRange] = useState('');
  const [newTimeout, setNewTimeout] = useState(60);
  const [lockReason, setLockReason] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');

  const handleAddIPRange = () => {
    if (!newIPRange.trim()) return;
    
    const currentRanges = settings?.allowed_ip_ranges || [];
    const newRanges = [...currentRanges, newIPRange.trim()];
    updateIPRestrictions(newRanges);
    setNewIPRange('');
  };

  const handleRemoveIPRange = (indexToRemove: number) => {
    const currentRanges = settings?.allowed_ip_ranges || [];
    const newRanges = currentRanges.filter((_, index) => index !== indexToRemove);
    updateIPRestrictions(newRanges);
  };

  const handleLockAdmin = () => {
    if (!selectedAdminId || !lockReason.trim()) return;
    toggleAdminLock(selectedAdminId, true, lockReason);
    setSelectedAdminId('');
    setLockReason('');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Admin Security Panel
        </CardTitle>
        <CardDescription>
          Configure and monitor administrative security settings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="settings">Security Settings</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
            <TabsTrigger value="emergency">Emergency Controls</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-6">
            {/* Session Management */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <Label className="text-sm font-normal">Session Timeout</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={newTimeout}
                  onChange={(e) => setNewTimeout(Number(e.target.value))}
                  placeholder="Minutes"
                  className="w-24"
                />
                <Button 
                  onClick={() => updateSessionTimeout(newTimeout)}
                  size="sm"
                  variant="accent"
                >
                  Update
                </Button>
              </div>
              {settings && (
                <p className="text-xs text-muted-foreground">
                  Current timeout: {settings.session_timeout_minutes} minutes
                </p>
              )}
            </div>

            <Separator />

            {/* IP Restrictions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <Label className="text-sm font-normal">IP Access Restrictions</Label>
              </div>
              <div className="flex gap-2">
                <Input
                  value={newIPRange}
                  onChange={(e) => setNewIPRange(e.target.value)}
                  placeholder="IP range (e.g., 192.168.1.0/24)"
                />
                <Button onClick={handleAddIPRange} size="sm" variant="accent">
                  Add Range
                </Button>
              </div>
              <div className="space-y-2">
                {settings?.allowed_ip_ranges?.map((range, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <code className="text-sm">{range}</code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveIPRange(index)}
                    >
                      Remove
                    </Button>
                  </div>
                )) || (
                  <p className="text-sm text-muted-foreground">No IP restrictions configured</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="monitoring" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                <Label className="text-sm font-normal">Admin Activity Log</Label>
              </div>
              <Button onClick={() => refetchAuditLogs()} size="sm" variant="outline">
                Refresh
              </Button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {auditLogs.length > 0 ? (
                auditLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">{log.action_type}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="text-sm">
                      {log.resource_type && (
                        <span className="text-muted-foreground">
                          Resource: {log.resource_type}
                        </span>
                      )}
                    </div>
                    {log.ip_address && (
                      <div className="text-xs text-muted-foreground">
                        IP: {String(log.ip_address)}
                      </div>
                    )}
                    {log.details && (
                      <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-x-auto">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No audit logs available
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="emergency" className="space-y-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <Label className="text-sm font-normal">Emergency Controls</Label>
            </div>

            {/* Admin Lockout */}
            <div className="space-y-3 p-4 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <Label className="text-sm font-normal">Lock Admin Account</Label>
              </div>
              <Input
                value={selectedAdminId}
                onChange={(e) => setSelectedAdminId(e.target.value)}
                placeholder="Admin User ID"
              />
              <Textarea
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                placeholder="Reason for locking account"
                rows={3}
              />
              <Button 
                onClick={handleLockAdmin}
                variant="destructive"
                disabled={!selectedAdminId || !lockReason.trim()}
              >
                Lock Admin Account
              </Button>
              <p className="text-xs text-muted-foreground">
                This will immediately revoke admin access for the specified user.
              </p>
            </div>

            {/* Current Status */}
            <div className="space-y-2">
              <Label className="text-sm font-normal">Current Security Status</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted rounded">
                  <div className="text-sm font-normal">Account Status</div>
                  <Badge variant={settings?.is_locked ? "destructive" : "secondary"}>
                    {settings?.is_locked ? "Locked" : "Active"}
                  </Badge>
                </div>
                <div className="p-3 bg-muted rounded">
                  <div className="text-sm font-normal">IP Restrictions</div>
                  <Badge variant="secondary">
                    {settings?.allowed_ip_ranges?.length || 0} ranges
                  </Badge>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};